import {
  flipkartPlacementBidViews,
  type FlipkartPlacementBidView,
  type FlipkartPlacementCostSource,
} from '@/components/flipkart/flipkartPlacementBidding';
import type { FlipkartAdsReportRow } from '@/api/client';
import type { DateRange } from '@/api/reportDateRanges';

export type FlipkartBaseMetrics = {
  spend: number;
  revenue: number;
  orders: number;
  impressions: number;
  clicks: number;
};

export type FlipkartDerivedMetrics = FlipkartBaseMetrics & {
  ctr: number | null;
  cpc: number | null;
  cvr: number | null;
  acos: number | null;
  roas: number | null;
};

export const FLIPKART_SYNOPSIS_METRIC_KEYS = [
  'spend',
  'revenue',
  'orders',
  'impressions',
  'clicks',
  'ctr',
  'cpc',
  'cvr',
  'acos',
  'roas',
] as const;

export type FlipkartSynopsisMetricKey = (typeof FLIPKART_SYNOPSIS_METRIC_KEYS)[number];

export type FlipkartSynopsisMetricFormat = 'money' | 'count' | 'percent' | 'roas';

export const FLIPKART_SYNOPSIS_METRICS: {
  key: FlipkartSynopsisMetricKey;
  label: string;
  format: FlipkartSynopsisMetricFormat;
}[] = [
  { key: 'spend', label: 'Spend', format: 'money' },
  { key: 'revenue', label: 'Revenue', format: 'money' },
  { key: 'orders', label: 'Orders', format: 'count' },
  { key: 'impressions', label: 'Impr.', format: 'count' },
  { key: 'clicks', label: 'Clicks', format: 'count' },
  { key: 'ctr', label: 'CTR', format: 'percent' },
  { key: 'cpc', label: 'CPC', format: 'money' },
  { key: 'cvr', label: 'CVR', format: 'percent' },
  { key: 'acos', label: 'ACOS', format: 'percent' },
  { key: 'roas', label: 'ROAS', format: 'roas' },
];

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const inrFormat = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 2,
});

const countFormat = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const percentFormat = new Intl.NumberFormat('en-IN', {
  style: 'percent',
  maximumFractionDigits: 2,
});

const roasFormat = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const deltaFormat = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 });

export function emptyBaseMetrics(): FlipkartBaseMetrics {
  return { spend: 0, revenue: 0, orders: 0, impressions: 0, clicks: 0 };
}

export function addBaseMetrics(
  left: FlipkartBaseMetrics,
  right: FlipkartBaseMetrics
): FlipkartBaseMetrics {
  return {
    spend: left.spend + right.spend,
    revenue: left.revenue + right.revenue,
    orders: left.orders + right.orders,
    impressions: left.impressions + right.impressions,
    clicks: left.clicks + right.clicks,
  };
}

function ratio(numerator: number, denominator: number): number | null {
  if (!(denominator > 0) || !Number.isFinite(numerator) || !Number.isFinite(denominator)) {
    return null;
  }
  return numerator / denominator;
}

/** Rates are computed from summed bases, not averaged across rows. */
export function deriveFlipkartMetrics(base: FlipkartBaseMetrics): FlipkartDerivedMetrics {
  return {
    ...base,
    ctr: ratio(base.clicks, base.impressions),
    cpc: ratio(base.spend, base.clicks),
    cvr: ratio(base.orders, base.clicks),
    acos: ratio(base.spend, base.revenue),
    roas: ratio(base.revenue, base.spend),
  };
}

/** Percent change from previous to current. Blank when previous is missing or zero. */
export function flipkartPercentChange(
  current: number | null,
  previous: number | null
): number | null {
  if (
    current == null ||
    previous == null ||
    previous === 0 ||
    !Number.isFinite(current) ||
    !Number.isFinite(previous)
  ) {
    return null;
  }
  return ((current - previous) / Math.abs(previous)) * 100;
}

export function dateInRange(date: string | null | undefined, range: DateRange): boolean {
  if (!date) return false;
  const day = date.slice(0, 10);
  return day >= range.startDate && day <= range.endDate;
}

export type FlipkartWindowSummary = {
  metrics: FlipkartDerivedMetrics;
  hasRows: boolean;
};

function baseFromRow(row: FlipkartAdsReportRow): FlipkartBaseMetrics {
  return {
    spend: row.spend || 0,
    revenue: row.revenue || 0,
    orders: row.orders || 0,
    impressions: row.impressions || 0,
    clicks: row.clicks || 0,
  };
}

export function summarizeFlipkartWindow(
  rows: FlipkartAdsReportRow[],
  range: DateRange
): FlipkartWindowSummary {
  let base = emptyBaseMetrics();
  let hasRows = false;
  for (const row of rows) {
    if (!dateInRange(row.date, range)) continue;
    hasRows = true;
    base = addBaseMetrics(base, baseFromRow(row));
  }
  return { metrics: deriveFlipkartMetrics(base), hasRows };
}

export type FlipkartSynopsisGroup = {
  key: string;
  label: string;
  secondary: string | null;
  current: FlipkartDerivedMetrics;
  previous: FlipkartDerivedMetrics;
  hasPrevious: boolean;
  /** From synced Flipkart campaign entities when this group is a campaign. */
  status?: string | null;
  dailyBudget?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  /** True when this row is joined to a stored campaign and can be edited. */
  campaignEditable?: boolean;
  /** Six placement bids when this row is joined to a stored ad group. Null when it is not. */
  placements?: FlipkartPlacementBidView[] | null;
};

/** Flipkart Ads stores live campaigns as status "LIVE" (not Amazon's ACTIVE). */
export const DEFAULT_FLIPKART_CAMPAIGN_STATUS = 'LIVE';

/** Flipkart ad-group statuses shown in the workspace filter. */
export const FLIPKART_AD_GROUP_STATUSES = [
  'SERVICEABLE',
  'ABORTED',
  'COMPLETED',
  'EXHAUSTED',
] as const;

export const DEFAULT_FLIPKART_AD_GROUP_STATUS = 'SERVICEABLE';

export function attachFlipkartCampaignMeta(
  groups: FlipkartSynopsisGroup[],
  campaignsById: Map<string, { status: string | null; daily_budget: number | null; start_date: string | null; end_date: string | null }>
): FlipkartSynopsisGroup[] {
  return groups.map((group) => {
    const campaign = campaignsById.get(group.key);
    return {
      ...group,
      status: campaign?.status ?? null,
      dailyBudget: campaign?.daily_budget ?? null,
      startDate: campaign?.start_date ?? null,
      endDate: campaign?.end_date ?? null,
      campaignEditable: Boolean(campaign),
    };
  });
}

export function attachFlipkartAdGroupPlacements(
  groups: FlipkartSynopsisGroup[],
  adGroupsById: ReadonlyMap<string, FlipkartPlacementCostSource & { status?: string | null }>
): FlipkartSynopsisGroup[] {
  return groups.map((group) => {
    const adGroup = adGroupsById.get(group.key);
    return {
      ...group,
      status: adGroup?.status ?? null,
      placements: adGroup ? flipkartPlacementBidViews(adGroup) : null,
    };
  });
}

export function flipkartSynopsisStatusOptions(
  groups: FlipkartSynopsisGroup[],
  alwaysInclude: string = DEFAULT_FLIPKART_CAMPAIGN_STATUS
): string[] {
  const values = new Set(groups.map((group) => group.status || 'Unknown'));
  values.add(alwaysInclude);
  return [...values].sort();
}

export function filterSynopsisGroupsByStatus(
  groups: FlipkartSynopsisGroup[],
  status: string
): FlipkartSynopsisGroup[] {
  if (status === 'all') return groups;
  return groups.filter((group) => (group.status || 'Unknown') === status);
}

export function flipkartWeekdayLabel(date: string | null | undefined): string | null {
  if (!date) return null;
  const parsed = new Date(`${date.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  const index = parsed.getDay() === 0 ? 6 : parsed.getDay() - 1;
  return WEEKDAYS[index] ?? null;
}

export function groupFlipkartSynopsisRows(
  rows: FlipkartAdsReportRow[],
  {
    current,
    previous,
    keyOf,
    labelOf,
    secondaryOf,
  }: {
    current: DateRange;
    previous: DateRange;
    keyOf: (row: FlipkartAdsReportRow) => string | null;
    labelOf?: (row: FlipkartAdsReportRow, key: string) => string;
    secondaryOf?: (row: FlipkartAdsReportRow, key: string) => string | null;
  }
): FlipkartSynopsisGroup[] {
  type Bucket = {
    key: string;
    label: string;
    secondary: string | null;
    current: FlipkartBaseMetrics;
    previous: FlipkartBaseMetrics;
    hasPrevious: boolean;
  };
  const buckets = new Map<string, Bucket>();

  for (const row of rows) {
    const key = keyOf(row)?.trim();
    if (!key) continue;
    // Catalog-seeded rows from the report API have no date and zero metrics.
    // Keep them so entities still appear when facts are empty or outside the window.
    const isCatalogSeed = !row.date;
    const inCurrent = dateInRange(row.date, current);
    const inPrevious = dateInRange(row.date, previous);
    if (!isCatalogSeed && !inCurrent && !inPrevious) continue;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        key,
        label: (labelOf ? labelOf(row, key) : key).trim() || key,
        secondary: secondaryOf?.(row, key)?.trim() || null,
        current: emptyBaseMetrics(),
        previous: emptyBaseMetrics(),
        hasPrevious: false,
      };
      buckets.set(key, bucket);
    } else if (!bucket.secondary && secondaryOf) {
      const next = secondaryOf(row, key)?.trim();
      if (next) bucket.secondary = next;
    }
    if (isCatalogSeed) continue;
    const metrics = baseFromRow(row);
    if (inCurrent) bucket.current = addBaseMetrics(bucket.current, metrics);
    if (inPrevious) {
      bucket.previous = addBaseMetrics(bucket.previous, metrics);
      bucket.hasPrevious = true;
    }
  }

  return [...buckets.values()].map((bucket) => ({
    key: bucket.key,
    label: bucket.label,
    secondary: bucket.secondary,
    current: deriveFlipkartMetrics(bucket.current),
    previous: deriveFlipkartMetrics(bucket.previous),
    hasPrevious: bucket.hasPrevious,
  }));
}

export function filterSynopsisGroups(
  groups: FlipkartSynopsisGroup[],
  query: string
): FlipkartSynopsisGroup[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return groups;
  return groups.filter((group) =>
    [group.label, group.secondary, group.status]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(needle)
  );
}

export type FlipkartSynopsisSortKey = 'label' | 'secondary' | FlipkartSynopsisMetricKey;

export function sortSynopsisGroups(
  groups: FlipkartSynopsisGroup[],
  key: FlipkartSynopsisSortKey,
  dir: 'asc' | 'desc'
): FlipkartSynopsisGroup[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...groups].sort((left, right) => {
    if (key === 'label' || key === 'secondary') {
      const a = (key === 'label' ? left.label : left.secondary || '').toLowerCase();
      const b = (key === 'label' ? right.label : right.secondary || '').toLowerCase();
      const compared = a.localeCompare(b);
      if (compared !== 0) return compared * factor;
      return left.label.localeCompare(right.label);
    }
    const a = left.current[key];
    const b = right.current[key];
    const an = a == null || !Number.isFinite(a) ? Number.NEGATIVE_INFINITY : a;
    const bn = b == null || !Number.isFinite(b) ? Number.NEGATIVE_INFINITY : b;
    if (an === bn) return left.label.localeCompare(right.label);
    return (an - bn) * factor;
  });
}

export function totalsFromGroups(groups: FlipkartSynopsisGroup[]): {
  current: FlipkartDerivedMetrics;
  previous: FlipkartDerivedMetrics;
  hasPrevious: boolean;
} {
  let current = emptyBaseMetrics();
  let previous = emptyBaseMetrics();
  let hasPrevious = false;
  for (const group of groups) {
    current = addBaseMetrics(current, group.current);
    previous = addBaseMetrics(previous, group.previous);
    if (group.hasPrevious) hasPrevious = true;
  }
  return {
    current: deriveFlipkartMetrics(current),
    previous: deriveFlipkartMetrics(previous),
    hasPrevious,
  };
}

export function formatFlipkartSynopsisMetric(
  value: number | null | undefined,
  format: FlipkartSynopsisMetricFormat
): string {
  if (value == null || !Number.isFinite(value)) return '—';
  switch (format) {
    case 'money':
      return inrFormat.format(value);
    case 'count':
      return countFormat.format(value);
    case 'percent':
      return percentFormat.format(value);
    case 'roas':
      return roasFormat.format(value);
  }
}

export function formatFlipkartSynopsisDelta(
  change: number | null
): { text: string; direction: 'up' | 'down' | 'flat' } | null {
  if (change == null || !Number.isFinite(change)) return null;
  if (change === 0) return { text: '0%', direction: 'flat' };
  const arrow = change > 0 ? '▲' : '▼';
  return {
    text: `${arrow} ${deltaFormat.format(Math.abs(change))}%`,
    direction: change > 0 ? 'up' : 'down',
  };
}

export function flipkartSynopsisLabel(value: string | null | undefined, fallback = 'Unknown'): string {
  const trimmed = value?.trim();
  return trimmed || fallback;
}
